import { useRef, useState, useEffect, useMemo, useCallback, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, LayoutGroup } from 'framer-motion';
import clsx from 'clsx';
import Icon from '../forms/icon.component';
import ChipFilters from './chip-filters.component';
import FilterDrawer from './filter-drawer.component';
import { FilterDef, FilterValues } from './chip-filters.types';
import styles from './filter-bar.module.scss';

export interface ChannelOption {
  value: string;
  label: string;
  iconPath?: string;
  group?: string;
}

type ChannelItem =
  | { type: 'single'; option: ChannelOption }
  | { type: 'group'; name: string; options: ChannelOption[] };

export interface FilterBarProps {
  defs: FilterDef[];
  value: FilterValues;
  onChange: (next: FilterValues) => void;
  channelOptions?: ChannelOption[];
  selectedChannel?: string;
  onChannelChange?: (channel: string) => void;
  extraEnd?: ReactNode;
  onDraftChange?: (draft: FilterValues) => void;
  className?: string;
}

export default function FilterBar({
  defs,
  value,
  onChange,
  channelOptions,
  selectedChannel,
  onChannelChange,
  extraEnd,
  onDraftChange,
  className
}: FilterBarProps) {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollFade, setScrollFade] = useState<'none' | 'right' | 'left' | 'both'>('none');
  const [showDrawer, setShowDrawer] = useState(false);

  const activeCount = useMemo(() => {
    const defKeys = new Set(defs.map((d) => d.key));
    return Object.entries(value).filter(([k, v]) => {
      if (!defKeys.has(k)) return false;
      if (!v) return false;
      if (Array.isArray(v)) return v.length > 0;
      return v !== '';
    }).length;
  }, [value, defs]);

  const handleClearAll = useCallback(() => {
    const cleared: FilterValues = {};
    for (const def of defs) cleared[def.key] = undefined;
    onChange(cleared);
  }, [defs, onChange]);

  useEffect(() => {
    if (!showDrawer) return;
    const mq = window.matchMedia('(min-width: 576px)');
    const handler = (e: MediaQueryListEvent) => {
      if (e.matches) setShowDrawer(false);
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [showDrawer]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const check = () => {
      const left = el.scrollLeft > 1;
      const right = el.scrollLeft < el.scrollWidth - el.clientWidth - 1;
      setScrollFade(left && right ? 'both' : left ? 'left' : right ? 'right' : 'none');
    };
    check();
    el.addEventListener('scroll', check);
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', check);
      ro.disconnect();
    };
  }, [defs, value]);

  const hasChannels = channelOptions && channelOptions.length > 0;
  const hasFilterContent = defs.length > 0 || !!extraEnd;
  const channelItems = useMemo<ChannelItem[]>(() => {
    if (!channelOptions) return [];
    const groups = new Map<string, ChannelOption[]>();
    const order: ChannelItem[] = [];
    for (const option of channelOptions) {
      if (!option.group) {
        order.push({ type: 'single', option });
        continue;
      }
      const existing = groups.get(option.group);
      if (existing) {
        existing.push(option);
      } else {
        const bucket = [option];
        groups.set(option.group, bucket);
        order.push({ type: 'group', name: option.group, options: bucket });
      }
    }
    return order;
  }, [channelOptions]);

  const renderChannelBtn = (ch: ChannelOption, stripGroupPrefix = false) => {
    const isActive = selectedChannel === ch.value;
    const text =
      stripGroupPrefix && ch.group
        ? ch.label.replace(new RegExp(`^${ch.group}\\s+`, 'i'), '')
        : ch.label;
    return (
      <button
        key={ch.value}
        type="button"
        aria-pressed={isActive}
        onClick={() => onChannelChange?.(ch.value)}
        className={clsx(styles.channelBtn, isActive && styles.channelBtnActive)}
        title={ch.label}
      >
        {isActive && (
          <motion.span
            layoutId="channelActiveBg"
            className={styles.channelActiveBg}
            transition={{ type: 'spring', stiffness: 480, damping: 36 }}
          />
        )}
        {ch.iconPath && (
          <img
            src={ch.iconPath}
            alt={ch.label}
            width={18}
            height={18}
            style={{ objectFit: 'contain' }}
          />
        )}
        <span className={styles.channelBtnLabel}>{text}</span>
      </button>
    );
  };

  return (
    <>
      <div className={clsx(styles.container, className)}>
        {hasChannels && (
          <div className={styles.channelRow} role="group" aria-label="Tipo">
            <span className={styles.channelLabel}>Tipo</span>
            <div className={styles.channelButtons}>
              <LayoutGroup>
                {channelItems.map((item) =>
                  item.type === 'single' ? (
                    renderChannelBtn(item.option)
                  ) : (
                    <div
                      key={`group-${item.name}`}
                      className={styles.channelSubGroup}
                      role="group"
                      aria-label={item.name}
                    >
                      <span className={styles.channelSubGroupLabel}>{item.name}</span>
                      {item.options.map((ch) => renderChannelBtn(ch, true))}
                    </div>
                  )
                )}
              </LayoutGroup>
            </div>
          </div>
        )}

        {hasFilterContent && (
          <div className={styles.wrapper}>
            <span className={styles.label}>
              <Icon icon="ki-filter" size="fs-5" />
              {t('general.filters')}
            </span>
            <div className={styles.divider} />

            <button
              type="button"
              className={clsx(styles.mobileTrigger, activeCount > 0 && styles.mobileTriggerActive)}
              onClick={() => setShowDrawer(true)}
            >
              <Icon icon="ki-filter" size="fs-7" />
              {t('general.filters')}
              {activeCount > 0 && <span className={styles.mobileBadge}>{activeCount}</span>}
            </button>

            <div
              ref={scrollRef}
              className={clsx(
                styles.scrollWrap,
                scrollFade === 'right' && styles.fadeRight,
                scrollFade === 'left' && styles.fadeLeft,
                scrollFade === 'both' && styles.fadeBoth
              )}
            >
              <ChipFilters defs={defs} value={value} onChange={onChange} hideClearAll />
            </div>

            {activeCount > 0 && (
              <button
                type="button"
                className={styles.clearBtn}
                onClick={handleClearAll}
                aria-label={t('chip-filters.clearAll', { count: activeCount })}
              >
                <i className="ki-duotone ki-eraser fs-7 text-danger">
                  <span className="path1" />
                  <span className="path2" />
                </i>
                {t('chip-filters.clearAll', { count: activeCount })}
              </button>
            )}

            {extraEnd && <div className={styles.extraEnd}>{extraEnd}</div>}
          </div>
        )}
      </div>

      <FilterDrawer
        defs={defs}
        value={value}
        isOpen={showDrawer}
        onClose={() => setShowDrawer(false)}
        onChange={onChange}
        onDraftChange={onDraftChange}
      />
    </>
  );
}
